/**
 * A short walkthrough for a firm's IT lead: a quick look at the portal, then how to update one control.
 * National IT, the Board, and anyone who cannot update the firm do not see the button. It is not offered on
 * Leadership, the National view, or outside a firm page. Skipping or finishing is remembered in this browser.
 * The header button starts it again. The tour may leave the firm page. It does not type, send, or record anything.
 */
import { BTI_WAVE2_CONTROLS, workState } from '@grc/shared/vocabulary';
import { useCallback, useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApiClient } from './api';
import type { Me, FirmControlRow } from './types';

const STORAGE_KEY = 'grc.dashboard-tour';

interface Place {
  pathname: string;
  /** A key set to null must be absent. Other keys must match. */
  search?: Record<string, string | null>;
}

interface Stop {
  text: string;
  /** Undefined while the sample control is still loading. */
  place: (firm: string, controlId: string | null) => Place | undefined;
  target: () => HTMLElement | null;
}

const STOPS: readonly Stop[] = [
  {
    text: 'Home is the starting point for the program. The ways in are Security controls, Policy templates, How National can help, and the firm dashboard.',
    place: () => ({ pathname: '/' }),
    target: () => (onPath('/') ? navLink('Home') : null),
  },
  {
    text: 'Security controls is the catalogue of controls. Each control shows its title, what it covers, its area, and its priority.',
    place: () => ({ pathname: '/guidance' }),
    target: () => heading('Security controls'),
  },
  {
    text: 'Policy templates are the National templates a firm tailors. Download a blank template, or create one for your firm with your details filled in.',
    place: () => ({ pathname: '/guidance/policies' }),
    target: () => heading('Policy templates'),
  },
  {
    text: 'Guidance is where the written guidance lives. Each control is there in plain words, with how to put it in place.',
    place: () => ({ pathname: '/guidance/policies' }),
    target: () => (onPath('/guidance/policies') ? navLink('Guidance') : null),
  },
  {
    text: "This page is your firm's own dashboard. It shows where the firm stands, what needs attention first, and the plan to get there.",
    place: (firm) => ({ pathname: `/firm/${firm}`, search: { view: null } }),
    target: () => heading('Cyber resilience dashboard'),
  },
  {
    text: 'Summary shows where the firm stands and what needs attention first. It shows the baselines, maturity, and the areas, highest priority first.',
    place: (firm) => ({ pathname: `/firm/${firm}`, search: { view: null } }),
    target: () => byId('firm-tab-summary'),
  },
  {
    text: 'Self-assessment shows how far the firm has rated its controls. The counts are returned with questions, not sent to National IT, awaiting review, and reviewed by National IT.',
    place: (firm) => ({ pathname: `/firm/${firm}`, search: { view: 'self' } }),
    target: () => byId('firm-tab-self'),
  },
  {
    text: 'This is one control, opened so you can update it. About this control says what the control asks for, and the other tabs are Control assistant, CDW findings, and Risk register.',
    place: (firm, controlId) => (controlId ? { pathname: `/firm/${firm}/controls/${controlId}`, search: { tab: null } } : undefined),
    target: () => {
      const title = document.querySelector('main h1');
      if (!(title instanceof HTMLElement) || !shown(title)) return null;
      const name = title.textContent?.trim();
      if (!name || name === 'Each control' || name === 'Cyber resilience dashboard') return null;
      return title;
    },
  },
  {
    text: 'The Control assistant is where you update this control. You describe how it works, then set the status on the draft and send it to National IT.',
    place: (firm, controlId) => (controlId ? { pathname: `/firm/${firm}/controls/${controlId}`, search: { tab: 'assistant' } } : undefined),
    target: () => byId('assistant-h'),
  },
  {
    text: 'Describe how this control is implemented in your firm, in the message box. Start with how the control works today: which tool does it, which systems it covers, and how you check it.',
    place: (firm, controlId) => (controlId ? { pathname: `/firm/${firm}/controls/${controlId}`, search: { tab: 'assistant' } } : undefined),
    target: () => byId('assistant-message'),
  },
  {
    text: 'Start chat sends your explanation. The assistant checks it and ends with the one next step.',
    place: (firm, controlId) => (controlId ? { pathname: `/firm/${firm}/controls/${controlId}`, search: { tab: 'assistant' } } : undefined),
    target: () => buttonIn('#assistant', 'Start chat'),
  },
  {
    text: 'When the assistant has replied, use Send the assessment. On the assessment, set the status and send it to National IT.',
    place: (firm, controlId) => (controlId ? { pathname: `/firm/${firm}/controls/${controlId}`, search: { tab: 'assistant' } } : undefined),
    target: () => document.querySelector('#assistant .assistant-steps li:nth-child(3)') as HTMLElement | null,
  },
];

type Memory = 'done' | 'skipped';

function readMemory(): Memory | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'done' || value === 'skipped' ? value : null;
  } catch {
    return 'skipped';
  }
}

function writeMemory(value: Memory) {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* the tour still closes; this browser will offer it again next time */
  }
}

/** The firm in the address, when this person is its IT lead and is not in a national or Board role. */
export function itLeadFirm(me: Me, pathname: string): string | null {
  if (me.tiers.national || me.tiers.board) return null;
  const match = /^\/firm\/([^/]+)/.exec(pathname);
  if (!match?.[1]) return null;
  let code = match[1];
  try {
    code = decodeURIComponent(code);
  } catch {
    /* keep the raw segment */
  }
  const firm = me.firms.find((f) => f.firmCode === code);
  return firm?.canWrite ? code : null;
}

/**
 * A Wave 2 control that already has an unsent draft, when the worklist has one.
 * Otherwise the first Wave 2 control. The tour only opens that control. It does not draft or send anything.
 */
export function sampleControlId(controls: readonly FirmControlRow[]): string {
  const wave2 = BTI_WAVE2_CONTROLS.filter((id) => controls.some((c) => c.controlId === id));
  const drafted = (id: string) => {
    const row = controls.find((c) => c.controlId === id);
    return Boolean(row && workState(row) === 'draft');
  };
  return wave2.find(drafted) ?? wave2[0] ?? controls[0]?.controlId ?? BTI_WAVE2_CONTROLS[0] ?? '5.4';
}

function firmPath(pathname: string) {
  return pathname.replace(/\/$/, '') || '/';
}

function onPath(pathname: string) {
  return firmPath(window.location.pathname) === pathname;
}

function onFirmPath(pathname: string, firmCode: string) {
  const path = firmPath(pathname);
  const home = `/firm/${firmCode}`;
  return path === home || path.startsWith(`${home}/`);
}

function placeMatches(place: Place) {
  if (firmPath(window.location.pathname) !== place.pathname) return false;
  if (!place.search) return true;
  const now = new URLSearchParams(window.location.search);
  for (const [key, value] of Object.entries(place.search)) {
    const current = now.get(key);
    if (value === null) {
      if (current !== null) return false;
    } else if (current !== value) return false;
  }
  return true;
}

function toHref(place: Place) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(place.search ?? {})) {
    if (value !== null) params.set(key, value);
  }
  const query = params.toString();
  return query ? `${place.pathname}?${query}` : place.pathname;
}

function reducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function shown(el: HTMLElement | null): el is HTMLElement {
  if (!el || el.closest('[hidden]')) return false;
  const rect = el.getBoundingClientRect();
  return rect.width >= 2 && rect.height >= 2;
}

function byId(id: string): HTMLElement | null {
  const el = document.getElementById(id);
  return el instanceof HTMLElement && shown(el) ? el : null;
}

function heading(title: string): HTMLElement | null {
  const el = document.querySelector('main h1');
  return el instanceof HTMLElement && shown(el) && el.textContent?.trim() === title ? el : null;
}

function navLink(name: string): HTMLElement | null {
  for (const link of document.querySelectorAll<HTMLElement>('header nav.nav a')) {
    if (link.textContent?.trim() === name && shown(link)) return link;
  }
  return null;
}

/** A button in the assistant. Never the tour's own buttons, and never clicked by the tour. */
function buttonIn(scope: string, name: string): HTMLElement | null {
  const root = document.querySelector(scope);
  if (!root) return null;
  for (const button of root.querySelectorAll('button')) {
    if (button.textContent?.trim() === name && shown(button)) return button;
  }
  return null;
}

interface Hole {
  top: number;
  left: number;
  width: number;
  height: number;
}

function holeFor(el: HTMLElement): Hole {
  const pad = 6;
  let rect = el.getBoundingClientRect();
  // A heading stretches across the row. Ring the words, so the dialog can sit beside them.
  if (/^H[1-3]$/.test(el.tagName)) {
    const range = document.createRange();
    range.selectNodeContents(el);
    const text = range.getBoundingClientRect();
    if (text.width >= 2 && text.height >= 2) rect = text;
  }
  return { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 };
}

function shades(hole: Hole | null): CSSProperties[] {
  if (!hole) return [{ inset: 0 }];
  const top = Math.max(0, hole.top);
  const left = Math.max(0, hole.left);
  const right = Math.max(0, window.innerWidth - (hole.left + hole.width));
  const bottom = Math.min(window.innerHeight, Math.max(0, hole.top + hole.height));
  return [
    { top: 0, left: 0, right: 0, height: top },
    { top, left: 0, width: left, height: Math.max(0, bottom - top) },
    { top, right: 0, width: right, height: Math.max(0, bottom - top) },
    { top: bottom, left: 0, right: 0, bottom: 0 },
  ].filter((shade) => shade.height !== 0 && shade.width !== 0);
}

export function DashboardTour({ firmCode, showButton }: { firmCode: string; showButton: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const call = useApiClient();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef(false);
  const pendingFocus = useRef(false);
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [run, setRun] = useState(0);
  const [controlId, setControlId] = useState<string | null>(null);

  const begin = useCallback(() => {
    setStep(0);
    setActive(true);
    activeRef.current = true;
    setRun((n) => n + 1);
  }, []);

  const close = useCallback(
    (how: Memory) => {
      if (!activeRef.current) return;
      activeRef.current = false;
      writeMemory(how);
      setActive(false);
      if (onFirmPath(window.location.pathname, firmCode)) {
        requestAnimationFrame(() => buttonRef.current?.focus());
      } else {
        pendingFocus.current = true;
        navigate(`/firm/${firmCode}`);
      }
    },
    [firmCode, navigate],
  );

  useEffect(() => {
    if (!showButton || readMemory() !== null || activeRef.current) return;
    begin();
  }, [showButton, begin]);

  useEffect(() => {
    if (!pendingFocus.current || !showButton || !buttonRef.current) return;
    pendingFocus.current = false;
    buttonRef.current.focus();
  }, [showButton, location.pathname]);

  useEffect(() => {
    if (!active) return;
    let cancel = false;
    call<{ controls: FirmControlRow[] }>(`/firms/${firmCode}/controls`)
      .then((data) => {
        if (!cancel) setControlId(sampleControlId(data.controls));
      })
      .catch(() => {
        if (!cancel) setControlId(BTI_WAVE2_CONTROLS[0] ?? '5.4');
      });
    return () => {
      cancel = true;
    };
  }, [active, firmCode, call]);

  useEffect(() => {
    if (!active) return;
    const place = STOPS[step]?.place(firmCode, controlId);
    if (!place || placeMatches(place)) return;
    navigate(toHref(place));
  }, [active, step, run, firmCode, controlId, navigate]);

  useEffect(() => {
    if (!active) return;
    const id = requestAnimationFrame(() => dialogRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [active, step, location.pathname, location.search]);

  useEffect(() => {
    if (!active) return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      close('skipped');
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, close]);

  return (
    <>
      {showButton && (
        <button ref={buttonRef} type="button" className="pill tour-launch" aria-expanded={active} onClick={begin}>
          How to use the dashboard
        </button>
      )}
      {active &&
        createPortal(
          <TourDialog
            step={step}
            dialogRef={dialogRef}
            onNext={() => {
              if (step >= STOPS.length - 1) close('done');
              else setStep((current) => current + 1);
            }}
            onSkip={() => close('skipped')}
          />,
          document.body,
        )}
    </>
  );
}

function TourDialog({
  step,
  dialogRef,
  onNext,
  onSkip,
}: {
  step: number;
  dialogRef: React.RefObject<HTMLDivElement | null>;
  onNext: () => void;
  onSkip: () => void;
}) {
  const textId = useId();
  const stepId = useId();
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [frame, setFrame] = useState<{ dock: boolean; hole: Hole | null; dialog: CSSProperties; shades: CSSProperties[] }>({
    dock: true,
    hole: null,
    dialog: {},
    shades: [{ top: 0, left: 0, right: 0, bottom: 0 }],
  });

  useEffect(() => {
    const look = () => setTarget(STOPS[step]?.target() ?? null);
    look();
    const observer = new MutationObserver(look);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'class', 'id', 'aria-selected'] });
    return () => observer.disconnect();
  }, [step]);

  useEffect(() => {
    const node = dialogRef.current;
    let scrolled = false;
    let cleared = false;
    const place = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const dialog = node?.getBoundingClientRect();
      const dh = dialog?.height || 160;
      const dw = Math.min(352, vw - 24);
      const narrow = vw < 720;
      const current = target;
      if (current) current.classList.add('tour-target');
      if (current && !scrolled) {
        const rect = current.getBoundingClientRect();
        const limit = narrow ? vh - dh - 28 : vh - 16;
        if (rect.top < 72 || rect.bottom > limit || rect.left < 0 || rect.right > vw) {
          scrolled = true;
          current.scrollIntoView({ block: narrow ? 'start' : 'center', inline: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
        }
      }
      const hole = current ? holeFor(current) : null;
      const margin = 12;
      let dock = narrow || !hole;
      let dialogStyle: CSSProperties = {};
      if (hole && !narrow) {
        const below = vh - (hole.top + hole.height) - margin;
        const above = hole.top - margin;
        let top: number | null = null;
        if (below >= dh + margin) top = hole.top + hole.height + margin;
        else if (above >= dh + margin) top = hole.top - dh - margin;
        if (top === null) dock = true;
        else {
          let left = hole.left;
          if (left + dw > vw - margin) left = Math.max(margin, vw - dw - margin);
          if (top + dh > vh - margin) top = Math.max(margin, vh - dh - margin);
          dialogStyle = { top, left, width: dw };
        }
      }
      const overlapsDock = Boolean(dock && hole && hole.top + hole.height > vh - dh - 20);
      if (overlapsDock && current && !cleared) {
        cleared = true;
        current.scrollIntoView({ block: 'start', inline: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
      }
      const next = {
        dock,
        hole: overlapsDock ? null : hole,
        dialog: dialogStyle,
        shades: shades(overlapsDock ? null : hole),
      };
      setFrame(next);
    };
    place();
    const observer = new ResizeObserver(place);
    if (node) observer.observe(node);
    if (target) observer.observe(target);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      target?.classList.remove('tour-target');
      observer.disconnect();
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [dialogRef, target, step]);

  const trap = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;
    const root = dialogRef.current;
    if (!root) return;
    const items = [...root.querySelectorAll<HTMLElement>('button:not([disabled])')];
    const firstButton = items[0];
    const lastButton = items[items.length - 1];
    if (!firstButton || !lastButton) return;
    if (event.shiftKey && (document.activeElement === firstButton || document.activeElement === root)) {
      event.preventDefault();
      lastButton.focus();
    } else if (!event.shiftKey && document.activeElement === lastButton) {
      event.preventDefault();
      firstButton.focus();
    }
  };

  const last = step >= STOPS.length - 1;
  return (
    <div className="tour">
      {frame.shades.map((style, i) => (
        <div key={i} className="tour-shade" style={style} aria-hidden="true" />
      ))}
      {frame.hole && <div className="tour-ring" style={frame.hole} aria-hidden="true" />}
      <div
        ref={dialogRef}
        className={frame.dock ? 'tour-dialog docked' : 'tour-dialog'}
        style={frame.dock ? undefined : frame.dialog}
        id="dashboard-tour"
        role="dialog"
        aria-modal="true"
        aria-labelledby={textId}
        aria-describedby={stepId}
        tabIndex={-1}
        onKeyDown={trap}
      >
        <p id={textId}>{STOPS[step]?.text}</p>
        <p id={stepId} className="tour-step">
          {step + 1} of {STOPS.length}
        </p>
        <div className="tour-actions">
          <button type="button" className="pill secondary-dark" onClick={onSkip}>
            Skip
          </button>
          <button type="button" className="pill" onClick={onNext}>
            {last ? 'Done' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
